import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone } from "lucide-react";
import { BRAND } from "@/data/site";

const TEL = BRAND.phone.replace(/\s/g, ""); // +918537942196
const WA = BRAND.whatsapp.replace(/[^\d]/g, ""); // 918537942196
const DISPLAY = BRAND.phone; // "+91 85379 42196"

// Inline WhatsApp glyph — official-style logo for the FAB
const WhatsAppIcon = ({ className = "", size = 22 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.001 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.257.596 4.463 1.727 6.4L3.2 28.8l6.567-1.72a12.75 12.75 0 0 0 6.234 1.611h.005c7.068 0 12.8-5.73 12.8-12.8 0-3.42-1.331-6.634-3.749-9.052A12.71 12.71 0 0 0 16.001 3.2Zm0 23.32h-.004a10.61 10.61 0 0 1-5.407-1.48l-.388-.23-4.036 1.057 1.076-3.933-.253-.404a10.63 10.63 0 0 1-1.62-5.63c0-5.876 4.78-10.66 10.66-10.66 2.847 0 5.523 1.11 7.535 3.124a10.58 10.58 0 0 1 3.117 7.54c0 5.876-4.78 10.616-10.68 10.616Zm5.83-7.94c-.32-.16-1.888-.933-2.18-1.04-.293-.107-.507-.16-.72.16-.213.32-.826 1.04-1.013 1.253-.187.214-.373.24-.693.08-.32-.16-1.35-.497-2.573-1.586-.95-.847-1.593-1.893-1.78-2.213-.187-.32-.02-.494.14-.653.144-.144.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.734-.986-2.373-.26-.626-.526-.54-.72-.55l-.613-.01c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667 0 1.573 1.146 3.093 1.306 3.306.16.213 2.253 3.44 5.466 4.826.764.33 1.36.527 1.826.674.767.244 1.466.21 2.02.128.616-.093 1.888-.771 2.153-1.517.267-.746.267-1.386.187-1.517-.08-.133-.293-.213-.613-.373Z"
    />
  </svg>
);

export default function MobileFloatingCTA() {
  const [open, setOpen] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div
      data-testid="mobile-floating-cta"
      className="md:hidden fixed right-4 z-40 pointer-events-none"
      style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
    >
      {/* Dismiss backdrop when open */}
      <AnimatePresence>
        {open && (
          <motion.button
            data-testid="mobile-cta-backdrop"
            aria-label="Close contact menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 -z-10 bg-slate-900/25 backdrop-blur-[2px] pointer-events-auto"
          />
        )}
      </AnimatePresence>

      {/* Contact options — anchored just above the FAB */}
      <div className="relative flex flex-col items-end gap-3">
        <AnimatePresence>
          {open && (
            <motion.div
              key="options"
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.96 }}
              transition={{ duration: 0.28, ease: [0.2, 0.7, 0.15, 1] }}
              className="pointer-events-auto flex flex-col items-end gap-2.5 mb-1"
            >
              <motion.a
                data-testid="mobile-cta-call"
                href={`tel:${TEL}`}
                aria-label={`Call Datorra India at ${DISPLAY}`}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.25, delay: 0.06 }}
                onClick={() => setOpen(false)}
                className="group inline-flex items-center gap-3 rounded-full bg-slate-900 text-white pl-4 pr-5 py-3 shadow-xl shadow-slate-900/20 active:scale-95 transition-transform"
              >
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                  <Phone className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-[0.18em] text-blue-200">Call Now</span>
                  <span className="block text-sm font-medium">{DISPLAY}</span>
                </span>
              </motion.a>

              <motion.a
                data-testid="mobile-cta-whatsapp"
                href={`https://wa.me/${WA}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Chat on WhatsApp at ${DISPLAY}`}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.25, delay: 0.02 }}
                onClick={() => setOpen(false)}
                className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] text-white pl-4 pr-5 py-3 shadow-xl shadow-green-500/25 active:scale-95 transition-transform"
              >
                <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
                  <WhatsAppIcon size={16} />
                </span>
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-[0.18em] text-white/80">WhatsApp Now</span>
                  <span className="block text-sm font-medium">{DISPLAY}</span>
                </span>
              </motion.a>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main FAB */}
        <motion.button
          data-testid="mobile-cta-fab"
          aria-label={open ? "Close contact options" : "Open contact options"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          whileTap={{ scale: 0.92 }}
          className="pointer-events-auto relative flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-2xl shadow-green-500/40 focus:outline-none"
        >
          {/* Pulse ring — only when closed */}
          {!open && (
            <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
          )}
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span
                key="x"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="relative"
              >
                <X className="w-6 h-6" strokeWidth={2} />
              </motion.span>
            ) : (
              <motion.span
                key="wa"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="relative"
              >
                <WhatsAppIcon size={26} />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </div>
  );
}
